/**
 * The account as the shell sees it (ADR 0034): whether someone is logged in, and what the airship holds by the
 * server's last word (`GET /api/me`: the eggs, the hatching jobs, the roster). It replaces slice 7a's stand-in store
 * (`ship-store.ts`, `ab.ship`, deleted with ADR 0033 D3's "Expires"): **nothing here persists game state** (the
 * session token is `api.ts`'s; the time-of-day, tier and language choices are per-device preferences), and nothing
 * is decided here: placing an egg and hatching it are the server's answers (`POST /api/jobs/hatch`,
 * `POST /api/jobs/:id/claim`); `@ab/session`'s job reducer only draws (a job's state and time left at the server's
 * time, `clock.ts`).
 *
 * - **Status:** `checking` (a saved token is being tried), `signed_in`, `signed_out` (the login shows), or `guest`
 *   (development's `?scene=` without a token, the tests' hook: no account, the ship empty). **Any** request answered
 *   `unauthorized` signs out (`api.ts` `onUnauthorized`): the shell shows the login over whatever is on screen (a
 *   scene, a run, a hatch); the run stays on the server.
 * - **The run being played** (`run()`, after a sign-in: `checkRun`, `GET /api/runs/current`): `checking` and `failed`
 *   (no answer: the shell holds the town under a retry row) until the server has answered; a run this build can play
 *   is handed to the resume listener (the flow plays it); one it cannot (another `coreVersion` or content hash) is
 *   `stuck`: kept on the server, flying refused, and the player either updates the game or gives it up
 *   (`giveUpRun`, `POST /api/runs/:id/abandon`, after a confirmation).
 * - **Guests:** a hatchling stays standing where it hatched (its scene and the hatch spot) until the traveler leaves
 *   the ship (`clearGuests`); presentation only, kept in memory for the page's life.
 * - **Keys:** placing an egg and each job's claim keep their idempotency key (a uuid) until the server answers, so
 *   trying again after "No connection." sends the same key and can never place or hatch twice. A claim the server
 *   granted keeps its key **and** the server's result until `GET /api/me` has read the ship again: a failed read says
 *   so (the claim stands), and the next try reads again instead of claiming again (a new key would only get
 *   `already_claimed`).
 * - **A temporary password** (ADR 0038 D8: an admin reset it): the login's answer says `mustChangePassword`, and
 *   `mustChangePassword()` holds the page under the new-password panel until `changePassword` has set a new one (the
 *   password typed at the login is kept in memory for that request only, never stored; the temporary one again is
 *   refused here, `same_password`, as the server would). The server refuses every play route meanwhile, so the run
 *   check waits for the new password (`main.tsx`). The panel closes as soon as the server has the new password: the
 *   account's read after it may fail (no answer), and then the run check reads it again under its Try again row
 *   (`stale`). An answer lost on the way (no answer, or `bad_credentials` because an earlier try did land) is settled
 *   by `GET /api/me`: `mustChangePassword` false means the new password is set. A saved session that still needs a new
 *   password (a reload before choosing one) is logged out, so the player logs in again with the temporary one and gets
 *   the panel.
 * - **Creating an account** needs the invite code an admin issued (ADR 0038 D10) and, since slice 8h (ADR 0042), a
 *   time zone (the login's list, the device's zone first): its game day begins at 05:00 there, for good.
 * - **The mailbox and the wallet (slice 8c, ADR 0039):** every read of the account reads the mail too, after it
 *   (`GET /api/mail`: the list and the unread count; a failed mail read keeps the last one; after, so the daily login
 *   reward the day's first `GET /api/me` sends is listed, slice 8e), and the wallet's skip tickets by tier and each
 *   job's skip plan at the server's time come with `GET /api/me`. The list is read again on its own (`loadMail`) when Mail
 *   opens and on arriving in a scene (the town, boarding), so staff mail sent meanwhile shows. Opening a mail marks it
 *   read; a skip keeps its idempotency key until the server answers. A claim keeps its key until the server answers
 *   and, once granted, its key **and** the server's answer until both `GET /api/me` and `GET /api/mail` have been read
 *   again: the answer (the mail claimed, the wallet, the stacks) shows at once, and a failed read says so
 *   (`claimPending`) and the next try reads again instead of claiming again. `takeMailBanner` is true once a session
 *   per account, the first time unread mail is known (the scene shows "You have mail" then): the flag is kept in
 *   `sessionStorage` per account id (`ab.mail_banner.<id>`; a reload in the tab keeps it, another account has its
 *   own), in memory when the browser refuses storage.
 */
import type { Egg, Mail, MailClaimResponse, MailGrant, MeResponse, ResumableRun, ShownError, SkipPlan, StorageStack, TicketCounts } from '@ab/api-contract';
import type { RosterMonster, TimedJob } from '@ab/session';
import type { Api } from './api.ts';
import { type ServerTime, secondsOf } from './clock.ts';

export type AccountStatus = 'checking' | 'signed_in' | 'signed_out' | 'guest';

/** A hatchling standing where it hatched: the roster instance, its species, the scene and the hatch spot (an interactable id). */
export type ShipGuest = { readonly id: string; readonly species: string; readonly scene: string; readonly spot: string };

/** The airship by the server's last word, in the shapes the scene draws (`@ab/session`'s jobs and roster, in seconds). */
export type ShipState = {
  /** Eggs aboard (not incubating), oldest first. */
  readonly eggs: readonly Egg[];
  readonly jobs: readonly TimedJob[];
  readonly roster: readonly RosterMonster[];
  /** The skip tickets in the wallet by tier (slice 8c; tiers since slice 8e): every tier of the server's rules, 0 when none. */
  readonly tickets: TicketCounts;
  /** Each open job's skip plan at the time of the last read (the server's choice of tickets; null: ready). */
  readonly skipPrices: Readonly<Record<string, SkipPlan | null>>;
  /** Each open job's plans to choose by hand (slice 8h, ADR 0042: each tier alone that covers it, the server's list). */
  readonly skipOptions: Readonly<Record<string, readonly SkipPlan[]>>;
  /** The airship's storage, by item id (the packing panel at the wheel lists what a bag takes from it, slice 8f). */
  readonly storage: readonly StorageStack[];
};

/** The mailbox by the server's last word: newest first, and how many were never opened. */
export type MailState = { readonly mails: readonly Mail[]; readonly unread: number };

export type MailClaimOutcome = { readonly ok: true; readonly granted: MailGrant } | { readonly ok: false; readonly error: ShownError };

/**
 * The run the server keeps as being played, as far as this page knows: `none` (nothing to resume, already resumed,
 * given up, or not asked: a guest, `?scene=`), `checking`, `failed` (no answer: tried again with a tap), or `stuck` (a
 * run this build cannot play; `asking` while its question is on screen).
 */
export type RunCheck =
  | { readonly kind: 'none' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'failed'; readonly error: ShownError }
  | { readonly kind: 'stuck'; readonly run: ResumableRun; readonly asking: boolean };

export type ClaimOutcome = { readonly ok: true; readonly monster: RosterMonster } | { readonly ok: false; readonly error: ShownError };

/** A skip's answer (the slice 8h fix round): `refused`, the skip's refusal or no answer (null: paid); `read`, the account read after it (null: read). */
export type SkipOutcome = { readonly refused: ShownError | null; readonly read: ShownError | null };

export type AccountStore = {
  status(): AccountStatus;
  /** The logged-in account's name. */
  name(): string | null;
  /** The logged-in account's id (the packing panel's remembered bag is per account, slice 8f). */
  id(): string | null;
  /** Why the saved session could not be checked at boot (no answer): the login says it. Cleared by the next try. */
  lastError(): ShownError | null;
  state(): ShipState;
  guests(): readonly ShipGuest[];
  run(): RunCheck;
  subscribe(listener: () => void): () => void;
  readonly time: ServerTime;
  readonly api: Api;
  /** Reads the account again (`GET /api/me`); an `unauthorized` answer logs out. */
  refresh(): Promise<ShownError | null>;
  logIn(name: string, password: string): Promise<ShownError | null>;
  /** A new account with its invite code and time zone (slice 8h, ADR 0042: the game day's zone, for good). */
  create(name: string, password: string, inviteCode: string, timeZone: string): Promise<ShownError | null>;
  /** The password is a temporary one (ADR 0038 D8): the page asks for a new one before anything else. */
  mustChangePassword(): boolean;
  /**
   * Sets the new password (`PUT /api/me/password`, the login's password as the current one); `same_password` for the
   * temporary one again (nothing is sent). Null once the server has it, even if reading the account after failed.
   */
  changePassword(next: string): Promise<ShownError | 'same_password' | null>;
  logOut(): Promise<void>;
  /** Asks the server for the run being played (`run()` follows); a playable one goes to the resume listener. */
  checkRun(): Promise<void>;
  /** Who plays a resumable run (the flow). */
  onResume(listener: (run: ResumableRun) => void): void;
  /** A stuck run's question again (flying was refused for it). */
  askAboutRun(): void;
  /** The stuck run's question closed without an answer: the run is kept, flying stays refused. */
  dismissRunQuestion(): void;
  /** Gives the stuck run up on the server (the explicit give-up); the refusal or no answer when it could not. */
  giveUpRun(): Promise<ShownError | null>;
  /** Places the oldest egg of `species` aboard in an incubator. */
  place(species: string, slot: string): Promise<ShownError | null>;
  /** Claims a ready hatch (or, after a claim whose ship read failed, reads the ship again). */
  claim(job: string): Promise<ClaimOutcome>;
  /**
   * Skips an incubating job's time in skip tickets (slice 8c): `plan`, the tickets the player confirmed (slice 8h, ADR
   * 0042: the server spends exactly those; `not_enough_tickets` or `bad_plan`, nothing spent), or `auto`, the game's
   * choice planned by the server at its own time (the fix round). The ship is read again after; the answer says both
   * the skip's refusal and the read's (so a refused choice is shown again only from a read that landed).
   */
  skip(job: string, plan: TicketCounts | 'auto'): Promise<SkipOutcome>;
  mail(): MailState;
  /** Opens a mail (marks it read); the refusal or no answer when it could not. */
  openMail(id: string): Promise<ShownError | null>;
  /** Reads the mail list again (`GET /api/mail`); the last list stays when it fails. */
  loadMail(): Promise<ShownError | null>;
  /**
   * Claims what a mail carries (shown at once from the server's answer); the account and the mail are read again
   * after. A claim granted whose reads failed answers the read's refusal: trying again reads again, never claims again.
   */
  claimMail(id: string): Promise<MailClaimOutcome>;
  /** A claim the server granted whose account and mail reads have not both succeeded yet. */
  claimPending(id: string): boolean;
  /** True once a session, when unread mail is known (the arrival banner, slice 8c). */
  takeMailBanner(): boolean;
  addGuest(guest: ShipGuest): void;
  clearGuests(): void;
};

const empty: ShipState = { eggs: [], jobs: [], roster: [], tickets: {}, skipPrices: {}, skipOptions: {}, storage: [] };
const noMail: MailState = { mails: [], unread: 0 };

/**
 * "You have mail" once a session per account (slice 8c): `sessionStorage` per account id, so a reload in the tab does
 * not show it again and another account gets its own; in memory when the browser refuses storage (private modes).
 */
const bannerShown = (() => {
  const memory = new Set<string>();
  const key = (account: string): string => `ab.mail_banner.${account}`;
  return {
    has(account: string): boolean {
      if (memory.has(account)) return true;
      try {
        return sessionStorage.getItem(key(account)) === '1';
      } catch {
        return false;
      }
    },
    add(account: string): void {
      memory.add(account);
      try {
        sessionStorage.setItem(key(account), '1');
      } catch {
        // Kept in memory only.
      }
    },
  };
})();

/** Stacks merged by item, sorted by id: how storage lists them. */
const withStacks = (storage: readonly StorageStack[], added: readonly StorageStack[]): StorageStack[] => {
  const m = new Map(storage.map((s) => [s.item, s.count]));
  for (const s of added) m.set(s.item, (m.get(s.item) ?? 0) + s.count);
  return [...m].map(([item, count]) => ({ item, count })).sort((a, b) => (a.item < b.item ? -1 : a.item > b.item ? 1 : 0));
};
const none: RunCheck = { kind: 'none' };

/** The server's account as the scene's shapes: jobs and the roster in whole seconds of the server's time. */
const shipOf = (me: MeResponse): ShipState => ({
  eggs: me.eggs.filter((e) => e.status === 'aboard'),
  jobs: me.jobs.map((j) => ({ id: j.id, kind: 'hatch', subject: j.subject, slot: j.slot, startedAt: secondsOf(j.startedAt), readyAt: secondsOf(j.completesAt) })),
  roster: me.roster.map((m) => ({ id: m.id, species: m.species, level: m.level, joinedAt: secondsOf(m.hatchedAt), job: m.job ?? '' })),
  tickets: me.wallet.tickets,
  skipPrices: Object.fromEntries(me.jobs.map((j) => [j.id, j.skipPrice])),
  skipOptions: Object.fromEntries(me.jobs.map((j) => [j.id, j.skipOptions])),
  storage: me.storage,
});

export type AccountOptions = {
  /** Whether this build can play a run (its pinned `coreVersion` and content hash are this bundle's). */
  readonly playable: (run: ResumableRun) => boolean;
};

export const createAccountStore = (api: Api, time: ServerTime, start: AccountStatus, options: AccountOptions): AccountStore => {
  let status: AccountStatus = start;
  let name: string | null = null;
  /** The logged-in account's id (the mail banner's flag is per account). */
  let accountId: string | null = null;
  let lastError: ShownError | null = null;
  let ship = empty;
  let guests: readonly ShipGuest[] = [];
  let runCheck: RunCheck = none;
  let mustChange = false;
  /** The password the account just logged in with, kept in memory only while a new one must be chosen. */
  let loginPassword: string | null = null;
  /** The account's read after a new password failed: the run check reads it again first. */
  let stale = false;
  let resume: ((run: ResumableRun) => void) | null = null;
  const claimKeys = new Map<string, string>();
  /** Claims the server granted whose ship read has not succeeded yet: the job's monster. */
  const granted = new Map<string, RosterMonster>();
  const startKeys = new Map<string, string>();
  /** Each job's skip key and the choice it was sent with (`auto`, or the plan's tickets in a fixed order). */
  const skipKeys = new Map<string, { readonly key: string; readonly choice: string }>();
  const mailKeys = new Map<string, string>();
  /** Mail claims the server granted whose account and mail reads have not both succeeded: its answer. */
  const mailGranted = new Map<string, MailClaimResponse>();
  let mail = noMail;
  /** The last mail read's refusal (null: it was read): a claim is settled only once the mail is read too. */
  let mailError: ShownError | null = null;
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const l of [...listeners]) l();
  };
  const signedOut = (): void => {
    status = 'signed_out';
    name = null;
    accountId = null;
    ship = empty;
    mail = noMail;
    mailGranted.clear();
    guests = [];
    runCheck = none;
    mustChange = false;
    loginPassword = null;
    stale = false;
    granted.clear();
    notify();
  };
  // Every endpoint's `unauthorized` (a revoked or expired session) lands here: the login shows from any state.
  api.onUnauthorized(() => {
    if (status !== 'guest') signedOut();
  });
  const refresh = async (): Promise<ShownError | null> => {
    if (api.token() === null) {
      if (status !== 'guest') signedOut();
      return 'unauthorized';
    }
    // The account first, then the mail (slice 8e): the day's first `GET /api/me` sends the daily login reward, which
    // the mail read then lists.
    const r = await api.me();
    const mailRead = await api.mail();
    if (mailRead.ok) mail = { mails: mailRead.value.mails, unread: mailRead.value.unread };
    mailError = mailRead.ok ? null : mailRead.error;
    if (!r.ok) {
      // A saved token that cannot be checked (no answer) leaves the page at the login, which says why.
      if (status === 'checking') {
        lastError = r.error === 'unauthorized' ? null : r.error;
        signedOut();
      }
      return r.error;
    }
    if (r.value.mustChangePassword && loginPassword === null) {
      // A temporary password, and the page does not know it (a reload): log in again with it, then choose a new one.
      await api.logOut();
      signedOut();
      return 'unauthorized';
    }
    mustChange = r.value.mustChangePassword;
    if (!mustChange) loginPassword = null;
    lastError = null;
    status = 'signed_in';
    name = r.value.account.name;
    accountId = r.value.account.id;
    ship = shipOf(r.value);
    notify();
    return null;
  };
  const loadMail = async (): Promise<ShownError | null> => {
    if (api.token() === null || status !== 'signed_in') return null;
    const r = await api.mail();
    if (!r.ok) return r.error;
    mail = { mails: r.value.mails, unread: r.value.unread };
    notify();
    return null;
  };
  const setRun = (next: RunCheck): void => {
    runCheck = next;
    notify();
  };
  const enter = async (r: Awaited<ReturnType<Api['logIn']>>): Promise<ShownError | null> => (r.ok ? refresh() : r.error);
  return {
    status: () => status,
    name: () => name,
    id: () => accountId,
    lastError: () => lastError,
    state: () => ship,
    guests: () => guests,
    run: () => runCheck,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    time,
    api,
    refresh,
    async logIn(n, password) {
      const r = await api.logIn(n, password);
      if (r.ok && r.value.mustChangePassword) loginPassword = password;
      return enter(r);
    },
    create: async (n, password, inviteCode, timeZone) => enter(await api.createAccount(n, password, inviteCode, timeZone)),
    mustChangePassword: () => mustChange,
    async changePassword(next) {
      if (loginPassword === null) return 'unauthorized';
      if (next.normalize('NFKC') === loginPassword.normalize('NFKC')) return 'same_password';
      const r = await api.changePassword(loginPassword, next);
      if (!r.ok) {
        if (r.error !== 'offline' && r.error !== 'error' && r.error !== 'bad_credentials') return r.error;
        // No answer, or the temporary password no longer works (an earlier try landed): the server's flag says.
        const settled = await api.me();
        if (!settled.ok || settled.value.mustChangePassword) return r.error;
      }
      // The server has the new password: the panel closes now, whatever the account's read after it says.
      // The account counts as unread until a read succeeds: the run check, which the closing panel starts, reads it
      // first.
      loginPassword = null;
      mustChange = false;
      stale = true;
      notify();
      if ((await refresh()) === null) stale = false;
      return null;
    },
    async logOut() {
      await api.logOut();
      signedOut();
    },
    async checkRun() {
      if (runCheck.kind === 'checking') return;
      setRun({ kind: 'checking' });
      if (stale) {
        // The account's read after a new password failed: read it first (its line and Try again if it fails again).
        const read = await refresh();
        if (read !== null) {
          setRun(read === 'unauthorized' ? none : { kind: 'failed', error: read });
          return;
        }
        stale = false;
      }
      const r = await api.currentRun();
      if (!r.ok) {
        // Unauthorized: signed out already (the login shows; the check runs again after it).
        setRun(r.error === 'unauthorized' ? none : { kind: 'failed', error: r.error });
        return;
      }
      const run = r.value.run;
      if (run === null) {
        setRun(none);
      } else if (options.playable(run)) {
        setRun(none);
        resume?.(run);
      } else {
        setRun({ kind: 'stuck', run, asking: true });
      }
    },
    onResume(listener) {
      resume = listener;
    },
    askAboutRun() {
      if (runCheck.kind === 'stuck' && !runCheck.asking) setRun({ ...runCheck, asking: true });
    },
    dismissRunQuestion() {
      if (runCheck.kind === 'stuck' && runCheck.asking) setRun({ ...runCheck, asking: false });
    },
    async giveUpRun() {
      if (runCheck.kind !== 'stuck') return null;
      const r = await api.abandonRun(runCheck.run.runId);
      if (!r.ok) return r.error;
      setRun(none);
      return null;
    },
    async place(species, slot) {
      const egg = ship.eggs.find((e) => e.species === species);
      if (egg === undefined) return 'no_egg';
      const key = startKeys.get(egg.id) ?? crypto.randomUUID();
      startKeys.set(egg.id, key);
      const r = await api.hatch(egg.id, slot, key);
      if (r.ok) startKeys.delete(egg.id);
      const again = await refresh();
      return r.ok ? again : r.error;
    },
    async claim(job) {
      let monster = granted.get(job);
      if (monster === undefined) {
        const key = claimKeys.get(job) ?? crypto.randomUUID();
        claimKeys.set(job, key);
        const r = await api.claim(job, key);
        // Refused or no answer: the key is kept, so trying again can never hatch twice.
        if (!r.ok) return r;
        const m = r.value.monster;
        monster = { id: m.id, species: m.species, level: m.level, joinedAt: secondsOf(m.hatchedAt), job: m.job ?? '' };
        granted.set(job, monster);
      }
      // The claim stands; the ship is read again before the hatch plays. If that read fails, the next try reads again.
      const read = await refresh();
      if (read !== null) return { ok: false, error: read };
      granted.delete(job);
      claimKeys.delete(job);
      return { ok: true, monster };
    },
    async skip(job, plan) {
      // The key belongs to the choice (the fix round): the same choice again (a retry after no answer) re-sends its
      // key, so it can never pay twice; another choice is a new request with a new key, so a stored answer for the
      // first choice never comes back as this one's. (Had the first landed, the job is ready and the second is
      // `already_ready`: nothing is paid twice either way.)
      const choice = plan === 'auto' ? 'auto' : JSON.stringify(Object.entries(plan).filter(([, n]) => n > 0).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
      const kept = skipKeys.get(job);
      const key = kept !== undefined && kept.choice === choice ? kept.key : crypto.randomUUID();
      skipKeys.set(job, { key, choice });
      const r = await api.skip(job, key, plan);
      // No answer or another refusal: the key is kept for this choice. Paid, or refused for its tickets (nothing was
      // spent: the next choice is a new request, slice 8h), the key is done.
      if (r.ok || r.error === 'not_enough_tickets' || r.error === 'bad_plan') skipKeys.delete(job);
      const read = await refresh();
      return { refused: r.ok ? null : r.error, read };
    },
    mail: () => mail,
    async openMail(id) {
      const r = await api.readMail(id);
      if (!r.ok) return r.error;
      const opened = r.value.mail;
      mail = { mails: mail.mails.map((m) => (m.id === id ? opened : m)), unread: mail.mails.filter((m) => m.id !== id && m.readAt === null).length };
      notify();
      return null;
    },
    loadMail,
    async claimMail(id) {
      let answer = mailGranted.get(id);
      if (answer === undefined) {
        const key = mailKeys.get(id) ?? crypto.randomUUID();
        mailKeys.set(id, key);
        const r = await api.claimMail(id, key);
        // Refused or no answer: the key is kept, so trying again can never claim twice.
        if (!r.ok) return r;
        answer = r.value;
        mailGranted.set(id, answer);
        // The server's answer shows at once: the mail claimed, the wallet, the stacks aboard.
        const claimed = answer.mail;
        mail = { mails: mail.mails.map((m) => (m.id === id ? claimed : m)), unread: mail.mails.filter((m) => m.id !== id && m.readAt === null).length };
        ship = { ...ship, tickets: answer.wallet.tickets, storage: withStacks(ship.storage, answer.granted.items) };
        notify();
      }
      // The claim stands; the account and the mail are read again. If a read fails, the next try reads again.
      const read = await refresh();
      if (read !== null) return { ok: false, error: read };
      if (mailError !== null) return { ok: false, error: mailError };
      mailGranted.delete(id);
      mailKeys.delete(id);
      return { ok: true, granted: answer.granted };
    },
    claimPending: (id) => mailGranted.has(id),
    takeMailBanner() {
      if (status !== 'signed_in' || accountId === null || mail.unread === 0 || bannerShown.has(accountId)) return false;
      bannerShown.add(accountId);
      return true;
    },
    addGuest(guest) {
      guests = [...guests.filter((g) => g.id !== guest.id), guest];
      notify();
    },
    clearGuests() {
      if (guests.length === 0) return;
      guests = [];
      notify();
    },
  };
};
